import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, TextField, MenuItem, CircularProgress, Tooltip } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import HelpIcon from "@material-ui/icons/Help";

import Home from "../Home/Home";
import { getAgent, updateAgent } from "../../Redux/Actions/agent";
import { selectAgent } from "../../Redux/Selectors";
import { AgentForm, toForm, toPayload, missingLabel } from "./form";
import { SendingCard, ExamplesCard, TestCard } from "./sections";
import "../ai-shared.css";

const TONES = [
  { value: "friendly", label: "Friendly" },
  { value: "professional", label: "Professional" },
  { value: "direct", label: "Direct" },
  { value: "consultative", label: "Consultative" },
];

const emptyResource = () => ({
  id: `resource-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
  name: "",
  url: "",
  note: "",
  enabled: true,
});

const AgentSettings: React.FC = () => {
  const dispatch = useDispatch();
  const agent: any = useSelector(selectAgent);
  const loading: boolean = useSelector((state: any) => state.agent.agentLoading);
  const error: string | null = useSelector((state: any) => state.agent.agentError);
  const syncId: number = useSelector((state: any) => state.agent.agentSyncId);
  const saving: boolean = useSelector((state: any) => state.agent.savingAgent);
  const [form, setForm] = useState<AgentForm | null>(() => (agent ? toForm(agent) : null));

  useEffect(() => {
    dispatch(getAgent());
  }, [dispatch]);

  // Replace the form with the server copy after a load or a successful save.
  useEffect(() => {
    if (agent) setForm(toForm(agent));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncId]);

  const setField = (field: keyof AgentForm, value: any) =>
    setForm((prev) => (prev ? { ...prev, [field]: value } : prev));

  const textField = (field: keyof AgentForm, label: string, extra: any = {}) => (
    <TextField
      label={label}
      value={(form as any)[field]}
      onChange={(event) => setField(field, event.target.value)}
      variant="outlined"
      size="small"
      fullWidth
      className="aiField"
      {...extra}
    />
  );

  const updateResource = (index: number, field: string, value: any) => {
    if (!form) return;
    const resources = [...form.resourceLinks];
    resources[index] = { ...resources[index], [field]: value };
    setField("resourceLinks", resources);
  };

  if (!form) {
    return (
      <Home>
        <div className="aiPage">
          {error && !loading ? (
            <div className="aiCard">
              <div className="aiCardTitle">Could not load the AI agent</div>
              <div className="aiNotice aiNotice--error">{error}</div>
              <Button variant="outlined" onClick={() => dispatch(getAgent())}>
                Retry
              </Button>
            </div>
          ) : (
            <div className="aiLoading">
              <CircularProgress />
            </div>
          )}
        </div>
      </Home>
    );
  }

  const readiness = (agent && agent.readiness) || {};
  const missing: string[] = Array.isArray(readiness.missing) ? readiness.missing : [];
  const examples: any[] = (agent && agent.approvedExamples) || [];

  return (
    <Home>
      <div className="aiPage">
        <div className="aiHeader">
          <div>
            <span className="heading">AI Reply Agent </span>
            <span className="headingTooltip">
              <Tooltip
                title="The agent reads new email replies and drafts an answer using the details below."
                placement="right-start"
                arrow
              >
                <HelpIcon />
              </Tooltip>
            </span>
            <div className="aiSub">
              {form.enabled ? "On" : "Off"} · version {agent?.version ?? "-"} · {examples.length}{" "}
              examples · {form.resourceLinks.length} resources
            </div>
          </div>
          <div className="aiActions">
            <Button
              variant="contained"
              className="aiPrimaryButton"
              onClick={() => dispatch(updateAgent(toPayload(form)))}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save agent"}
            </Button>
          </div>
        </div>

        {readiness.ready === false && (
          <div className="aiNotice aiNotice--warn">
            The agent will not draft replies automatically until these are filled in and saved:
            <ul>
              {(missing.length ? missing : ["Company name", "Company details"]).map((key) => (
                <li key={key}>{missingLabel(key)}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="aiGrid">
          <div className="aiCard">
            <div className="aiCardTitle">Company facts</div>
            <div className="aiCardHint">
              These are the only facts the agent may use. Anything not written here will not be
              invented.
            </div>
            {textField("name", "Agent name")}
            {textField("companyName", "Company name")}
            {textField("companyDetails", "Company details", {
              multiline: true,
              rows: 3,
              placeholder: "What the company does, who it serves, differentiators",
            })}
            {textField("offerDetails", "Offer details", {
              multiline: true,
              rows: 3,
              placeholder: "Plans, pricing, packages, guarantees",
            })}
            {textField("goal", "Goal of a reply", { placeholder: "e.g. book a 15 minute call" })}
          </div>

          <div className="aiCard">
            <div className="aiCardTitle">FAQs and policies</div>
            <div className="aiCardHint">
              Also treated as facts. Write answers the agent may give word for word.
            </div>
            {textField("faqs", "FAQs", {
              multiline: true,
              rows: 6,
              inputProps: { maxLength: 4000 },
              placeholder: "Q: Do you offer a free trial?\nA: Yes, 14 days, no card needed.",
              helperText: `${form.faqs.length}/4000`,
            })}
            {textField("policies", "Policies", {
              multiline: true,
              rows: 4,
              inputProps: { maxLength: 3000 },
              placeholder: "Refunds, pricing rules, booking rules",
              helperText: `${form.policies.length}/3000`,
            })}
          </div>

          <div className="aiCard">
            <div className="aiCardTitle">How the agent should reply</div>
            <div className="aiCardHint">
              Guidance may refine voice only. It can never override facts or the safety rules.
            </div>
            {textField("tone", "Tone", {
              select: true,
              children: TONES.map((tone) => (
                <MenuItem key={tone.value} value={tone.value}>
                  {tone.label}
                </MenuItem>
              )),
            })}
            {textField("instructions", "Instructions", {
              multiline: true,
              rows: 4,
              placeholder: "e.g. Always mention the free pilot. Never promise discounts.",
            })}
            {textField("guardrails", "Guardrails", {
              multiline: true,
              rows: 2,
              placeholder: "Extra limits, compliance notes",
            })}
            {textField("forbiddenTopicsText", "Never discuss (comma separated)")}
            <div className="aiRow">
              {textField("maxWords", "Max words", {
                type: "number",
                fullWidth: false,
                placeholder: "180",
              })}
              {textField("maxParagraphs", "Max paragraphs", {
                type: "number",
                fullWidth: false,
                placeholder: "3",
              })}
            </div>
            {textField("signature", "Signature (kept verbatim)", {
              multiline: true,
              rows: 2,
              placeholder: "Bob Smith\nAcme Inc",
            })}
            <label className="aiCheckRow">
              <input
                type="checkbox"
                checked={form.signatureIncluded}
                onChange={(event) => setField("signatureIncluded", event.target.checked)}
              />
              <span>Append the signature automatically</span>
            </label>
          </div>

          <SendingCard form={form} setField={setField} />
        </div>

        <div className="aiCard">
          <div className="aiCardTitle">Approved resources and links</div>
          <div className="aiCardHint">
            The agent may only reference these links. Any other URL is stripped before review.
          </div>
          {form.resourceLinks.map((resource: any, index: number) => (
            <div className="agentResourceRow" key={resource.id || index}>
              <TextField
                label="Name"
                value={resource.name || ""}
                onChange={(event) => updateResource(index, "name", event.target.value)}
                variant="outlined"
                size="small"
              />
              <TextField
                label="URL"
                value={resource.url || ""}
                onChange={(event) => updateResource(index, "url", event.target.value)}
                variant="outlined"
                size="small"
              />
              <TextField
                label="Note"
                value={resource.note || ""}
                onChange={(event) => updateResource(index, "note", event.target.value)}
                variant="outlined"
                size="small"
              />
              <label className="aiCheckRow" style={{ marginTop: 0 }}>
                <input
                  type="checkbox"
                  checked={resource.enabled !== false}
                  onChange={(event) => updateResource(index, "enabled", event.target.checked)}
                />
                <span>On</span>
              </label>
              <Button
                size="small"
                className="aiDangerText"
                aria-label="Remove resource"
                onClick={() =>
                  setField(
                    "resourceLinks",
                    form.resourceLinks.filter((_: any, i: number) => i !== index)
                  )
                }
              >
                <DeleteIcon fontSize="small" />
              </Button>
            </div>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setField("resourceLinks", [...form.resourceLinks, emptyResource()])}
          >
            Add resource
          </Button>
        </div>

        <ExamplesCard examples={examples} />
        <TestCard />
      </div>
    </Home>
  );
};

export default AgentSettings;
